import React from "react";
import { useNavigate } from "react-router-dom";

import { useAuth0 } from '@auth0/auth0-react';
import { Message } from "@arco-design/web-react";
import { FetchApiPromise } from '../Api/AuthenticatedApi';
import { DEFAULT_SORT } from './contactColumns';
import { filterToParam, forServer, requiredList } from '../Components/ContactFilter/contactFilter';

import "react-csv-importer/dist/index.css";

const ROWS_PER_PAGE = 20;

// UseLoadContacts pages in the contacts matching the search text and the
// rule-based filter (both start from the URL: initialFilter, initialSearch).
export function UseLoadContacts(initialFilter, initialSearch) {
    const [loadItemsLoading, setLoadItemsLoading] = React.useState(false);
    const [contactItems, setContactItems] = React.useState([]);
    const [hasNextPage, setHasNextPage] = React.useState(true);
    const [error, setError] = React.useState();

    const [shouldUpdateDataFromServer, setShouldUpdateDataFromServer] = React.useState([]);

    const [searchTerm, setSearchTerm] = React.useState(initialSearch || "");
    const [filter, setFilter] = React.useState(initialFilter || null);
    const [sort, setSort] = React.useState(DEFAULT_SORT);
    const [totalCount, setTotalCount] = React.useState(0);
    // A list every match must be on (else "all"): sent too, so "Signed up"
    // sorts and shows by when they joined that list.
    const contactListFilterId = requiredList(filter);

    // Goes up on each refresh. A reply from an earlier number is out of date and
    // is dropped, so it can't add rows to the fresh list.
    const refreshNumber = React.useRef(0);

    const { getAccessTokenSilently } = useAuth0();
    let history = useNavigate();

    function loadItemsFromServer(startCursor = 0) {
        return new Promise((resolve, reject) => {
            FetchApiPromise(getAccessTokenSilently,
                "/api/contacts/?" + new URLSearchParams({ page: startCursor / ROWS_PER_PAGE, count: ROWS_PER_PAGE, searchTerm, contactListFilterId, filter: filterToParam(forServer(filter)), sort }),
                ({ total_count, contacts }) => {
                    resolve({ newHasNextPage: contacts.length >= ROWS_PER_PAGE, newData: contacts, newTotalCountMatched: total_count })
                },
                (response) => {
                    history("/login");
                    reject(response);
                }, (response) => {
                    Message.error("Failed to load contacts");
                    reject(response);
                })
        });
    }


    // Allow the outside world to tell us to refresh
    React.useEffect(() => {

        if (shouldUpdateDataFromServer && shouldUpdateDataFromServer.length > 0) {
            shouldUpdateDataFromServer.forEach((update) => {
                // operation: "update", updated_record: updatedContact
                switch (update.operation) {
                    case "update":
                        if (update.updated_record) {
                            for (var i = 0; i < contactItems.length; i++) {
                                if (update.updated_record.id === contactItems[i].id) {
                                    Object.assign(contactItems[i], update.updated_record)
                                    break;
                                }
                            }
                        }
                        break;
                    case "refresh": {
                        const thisRefresh = ++refreshNumber.current;
                        setContactItems([]); // Clear existing items
                        setTotalCount(0); // clear existing total
                        setHasNextPage(true); // Reset hasNextPage
                        // Marked as loading so the table doesn't see an empty list
                        // and ask for the first page a second time.
                        setLoadItemsLoading(true);

                        loadItemsFromServer()
                            .then(({ newHasNextPage, newData, newTotalCountMatched }) => {
                                if (thisRefresh !== refreshNumber.current) return;
                                setHasNextPage(newHasNextPage);
                                setContactItems(newData);
                                setTotalCount(newTotalCountMatched)
                                setLoadItemsLoading(false);
                            })
                            .catch((error) => {
                                if (thisRefresh !== refreshNumber.current) return;
                                setError(error);
                                setLoadItemsLoading(false);
                            });
                        break;
                    }
                    default:
                        console.error("Unsupported operation ", update.operation);
                }
            });
            setShouldUpdateDataFromServer([]);
        }
    }, [shouldUpdateDataFromServer]); // eslint-disable-line react-hooks/exhaustive-deps



    async function loadMore() {
        const thisRefresh = refreshNumber.current;
        setLoadItemsLoading(true);
        try {
            const { newHasNextPage, newData, newTotalCountMatched } = await loadItemsFromServer(
                contactItems.length,
            );
            if (thisRefresh !== refreshNumber.current) return;
            setHasNextPage(newHasNextPage);
            setContactItems((current) => [...current, ...newData]);
            setTotalCount(newTotalCountMatched);
            setLoadItemsLoading(false);
        } catch (err) {
            if (thisRefresh !== refreshNumber.current) return;
            setError(err);
            setLoadItemsLoading(false);
        }
    }

    return { totalCount, contactListFilterId, filter, setFilter, searchTerm, setSearchTerm, sort, setSort, setShouldUpdateDataFromServer, loadItemsLoading, contactItems, hasNextPage, error, loadMore };
}

export default UseLoadContacts;